import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';

import { EnterpriseSubscriptionService } from '../enterprise-subscription/enterprise-subscription.service';
import {
    CompanyPlanSubscription,
    CompanyPlanUsage,
    EnterpriseSubscription,
    TrialLifecycleResponse
} from '../enterprise-subscription/enterprise-subscription.model';
import { SessionStorageService } from './session-storage.service';
import { ToolbarService } from './toolbar.service';

const ALL_MODULES = [
    'SHIPMENTS', 'ACCOUNTING', 'FLEET', 'GPS_TRACKING', 'CONTAINER_TRACKING', 'CUSTOMS_CLEARANCE',
    'GENERAL_LEDGER', 'POSM_PROJECT_TRACKING', 'CRM', 'WAREHOUSING', 'CONTRACTS', 'WORKFLOW_AUTOMATION',
    'PARTNER_MANAGEMENT', 'AI_CHAT', 'WASENDER', 'FUEL_MANAGEMENT', 'HR', 'AUDIT', 'API_PLATFORM', 'PROJECTS'
];

@Injectable({ providedIn: 'root' })
export class EnterpriseFeatureService {
    private enterpriseSubscriptionService = inject(EnterpriseSubscriptionService);
    private sessionStorageService = inject(SessionStorageService);
    private toolbarService = inject(ToolbarService);

    public subscriptions = signal<EnterpriseSubscription[]>([]);
    public planSubscription = signal<CompanyPlanSubscription | null>(null);
    public planUsage = signal<CompanyPlanUsage | null>(null);
    public trialLifecycle = signal<TrialLifecycleResponse | null>(null);
    public isLoaded = signal<boolean>(false);

    public isEnterpriseEnabled = computed(() => !!this.sessionStorageService.getCompany()?.enableEnterpriseFeatures);
    public activeModules = computed(() => this.toSyntheticModuleSubscriptions(this.planSubscription()));
    public hasModule = (_moduleCode: string) => computed(() => !!this.planSubscription());

    public isShipmentsActive = this.hasModule('SHIPMENTS');
    public isAccountingActive = this.hasModule('ACCOUNTING');
    public isGeneralLedgerActive = this.hasModule('GENERAL_LEDGER');
    public isCustomsClearanceActive = this.hasModule('CUSTOMS_CLEARANCE');
    public isFleetActive = this.hasModule('FLEET');
    public isGpsActive = this.hasModule('GPS_TRACKING');
    public isContainersActive = this.hasModule('CONTAINER_TRACKING');
    public isPosmActive = this.hasModule('POSM_PROJECT_TRACKING');
    public isCrmActive = this.hasModule('CRM');
    public isWarehousingActive = this.hasModule('WAREHOUSING');
    public isContractsActive = this.hasModule('CONTRACTS');
    public isWorkflowAutomationActive = this.hasModule('WORKFLOW_AUTOMATION');
    public isPartnerManagementActive = this.hasModule('PARTNER_MANAGEMENT');
    public isAiChatActive = this.hasModule('AI_CHAT');
    public isWasenderActive = this.hasModule('WASENDER');
    public isFuelManagementActive = this.hasModule('FUEL_MANAGEMENT');
    public isHrActive = this.hasModule('HR');
    public isAuditActive = this.hasModule('AUDIT');
    public isApiPlatformActive = this.hasModule('API_PLATFORM');
    public isProjectsActive = this.hasModule('PROJECTS');
    public isReadOnlyGrace = computed(() => false);

    public quotas = computed(() => {
        const plan = this.planSubscription()?.plan;
        return {
            officeUsers: plan?.officeUserLimit ?? 0,
            fieldAgents: plan?.driverLimit ?? 0,
            shipmentsLimit: plan?.shipmentsLimit ?? 0
        };
    });

    public getAllSubscriptions(): Observable<EnterpriseSubscription[]> {
        const company = this.sessionStorageService.getCompany() ?? this.toolbarService.customer()?.company;
        if (!company?.id) return of([]);
        return this.enterpriseSubscriptionService.getEffectiveCompanyPlan(company.id).pipe(
            tap(plan => this.applyPlan(plan)),
            map(plan => this.toSyntheticModuleSubscriptions(plan)),
            catchError(error => {
                console.error('Error loading company plan', error);
                this.isLoaded.set(true);
                return of([]);
            })
        );
    }

    public refreshPlanUsage(): Observable<CompanyPlanUsage | null> {
        const company = this.sessionStorageService.getCompany() ?? this.toolbarService.customer()?.company;
        if (!company?.id) return of(null);
        return this.enterpriseSubscriptionService.getCompanyPlanUsage(company.id).pipe(
            tap(usage => this.planUsage.set(usage)),
            catchError(error => {
                console.error('Error loading company plan usage', error);
                return of(null);
            })
        );
    }

    /** Compatibility bridge for callers that still request the former lifecycle DTO. */
    public refreshTrialLifecycle(): Observable<TrialLifecycleResponse | null> {
        const plan = this.planSubscription();
        if (!plan) return of(null);
        const lifecycle = this.lifecycleFromPlan(plan);
        this.trialLifecycle.set(lifecycle);
        sessionStorage.setItem('isTrialReadOnlyGrace', 'false');
        return of(lifecycle);
    }

    public syncLegacyStorage(_subscriptions: EnterpriseSubscription[]): void {
        const active = !!this.planSubscription();
        const fields: Record<string, string> = {
            isShipmentSubscribed: String(active), isAccountsSubscribed: String(active), isGeneralLedgerSubscribed: String(active),
            isCustomsClearanceSubscribed: String(active), isFleetSubscribed: String(active), isGPSTrackingSubscribed: String(active),
            isContainerTrackingTrackingSubscribed: String(active), isPosmTrackingSubscribed: String(active), isCrmSubscribed: String(active),
            isWarehousingSubscribed: String(active), isContractsSubscribed: String(active), isWorkflowAutomationSubscribed: String(active),
            isPartnerManagementSubscribed: String(active), isAiChatSubscribed: String(active), isWasenderSubscribed: String(active),
            isFuelManagementSubscribed: String(active), isHrSubscribed: String(active), isAuditSubscribed: String(active),
            isApiPlatformSubscribed: String(active), isProjectsSubscribed: String(active), enableEnterpriseAccounts: String(active),
            isTrialReadOnlyGrace: 'false'
        };
        Object.entries(fields).forEach(([key, value]) => sessionStorage.setItem(key, value));
    }

    getIsShipmentsSubscribed(): boolean { return this.isShipmentsActive(); }
    getIsAccountsSubscribed(): boolean { return this.isAccountingActive(); }
    getIsGeneralLedgerSubscribed(): boolean { return this.isGeneralLedgerActive(); }
    getIsCustomsClearanceSubscribed(): boolean { return this.isCustomsClearanceActive(); }
    getIsFleetSubscribed(): boolean { return this.isFleetActive(); }
    getIsGPSTrackingSubscribed(): boolean { return this.isGpsActive(); }
    getIsContainerTrackingTrackingSubscribed(): boolean { return this.isContainersActive(); }
    getIsPosmSubscribed(): boolean { return this.isPosmActive(); }
    getIsCrmSubscribed(): boolean { return this.isCrmActive(); }
    getIsWarehousingSubscribed(): boolean { return this.isWarehousingActive(); }
    getIsContractsSubscribed(): boolean { return this.isContractsActive(); }
    getIsWorkflowAutomationSubscribed(): boolean { return this.isWorkflowAutomationActive(); }
    getIsPartnerManagementSubscribed(): boolean { return this.isPartnerManagementActive(); }
    getIsAiChatSubscribed(): boolean { return this.isAiChatActive(); }
    getIsWasenderSubscribed(): boolean { return this.isWasenderActive(); }
    getIsFuelManagementSubscribed(): boolean { return this.isFuelManagementActive(); }
    getIsHrSubscribed(): boolean { return this.isHrActive(); }
    getIsAuditSubscribed(): boolean { return this.isAuditActive(); }
    getIsApiPlatformSubscribed(): boolean { return this.isApiPlatformActive(); }
    getIsProjectsSubscribed(): boolean { return this.isProjectsActive(); }
    getEnableEnterpriseAccounts(): boolean { return this.isEnterpriseEnabled() || !!this.planSubscription(); }

    private applyPlan(plan: CompanyPlanSubscription): void {
        const modules = this.toSyntheticModuleSubscriptions(plan);
        this.planSubscription.set(plan);
        this.subscriptions.set(modules);
        this.isLoaded.set(true);
        this.toolbarService.updateEnterpriseFeatures(modules);
        this.syncLegacyStorage(modules);
    }

    private toSyntheticModuleSubscriptions(plan: CompanyPlanSubscription | null): EnterpriseSubscription[] {
        if (!plan) return [];
        return ALL_MODULES.map(module => ({
            companyId: plan.companyId,
            module,
            startDate: plan.startedAt,
            trialEndDate: plan.trialEndsAt ?? undefined,
            endDate: plan.paidEndsAt ?? undefined,
            fees: plan.currentMonthlyPriceUsd,
            status: plan.status === 'TRIAL' ? 'TRIAL' : 'ACTIVE',
            paymentStatus: plan.paymentStatus ?? undefined,
            isTrial: plan.status === 'TRIAL',
            officeUsers: plan.plan.officeUserLimit ?? undefined,
            fieldAgents: plan.plan.driverLimit ?? undefined,
            shipmentsLimit: plan.plan.shipmentsLimit ?? undefined
        }));
    }

    private lifecycleFromPlan(plan: CompanyPlanSubscription): TrialLifecycleResponse {
        const trialEndsAt = plan.trialEndsAt ? new Date(plan.trialEndsAt) : null;
        const daysRemaining = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / 86_400_000)) : undefined;
        return {
            companyId: plan.companyId,
            status: plan.status === 'TRIAL' ? (daysRemaining! <= 7 ? 'TRIAL_EXPIRING' : 'TRIAL_ACTIVE')
                : plan.status === 'ACTIVE' ? 'PAID_ACTIVE' : 'FREE',
            trialStartAt: plan.startedAt,
            trialEndAt: plan.trialEndsAt ?? undefined,
            daysRemaining,
            readOnlyMode: false,
            freePlanActive: plan.planCode === 'FREE',
            paidActive: plan.status === 'ACTIVE',
            freeModules: ALL_MODULES,
            freeMonthlyRecordLimit: 100,
            freeOfficeUsers: 2,
            freeFleetDrivers: undefined,
            freeFleetVehicles: undefined,
            conversionActions: []
        };
    }
}
